import { UcoastEl } from '@/scripts/core/UcoastEl'
import {
	closestOptional,
	getAttributeOrThrow,
	qsaRequired,
	qsRequired,
	targetOptional,
} from '@/scripts/core/global'
import { type DynamicQuizStub } from '@/scripts/interactive-defer/dynamic-quiz-stub'

type QuizResult = {
	url: string
	responses: string[][]
}

export class DynamicQuiz extends UcoastEl {
	static htmlSelector = 'dynamic-quiz'
	currentQuestionEl: HTMLElement
	submitButton: HTMLButtonElement
	backButtons: NodeListOf<HTMLButtonElement>
	nextButtons: NodeListOf<HTMLButtonElement>
	questions: NodeListOf<HTMLFieldSetElement>
	currentQuestion: number
	formEl: HTMLFormElement
	resultData: QuizResult[]
	progressFillEl: HTMLElement
	titleText: string
	constructor() {
		super()
		this.currentQuestion = 1
		this.currentQuestionEl = qsRequired('[data-current-question]', this)
		this.submitButton = qsRequired('[data-quiz-submit]', this)
		this.backButtons = qsaRequired('[data-quiz-back]', this)
		this.nextButtons = qsaRequired('[data-quiz-next]', this)
		this.questions = qsaRequired('[data-question]', this)
		this.formEl = qsRequired('[data-form]', this)
		this.resultData = this.getResultData()
		this.onFocus = this.onFocus.bind(this)
		this.onSubmit = this.onSubmit.bind(this)
		this.titleText = qsRequired('[data-title]', this).innerHTML
		document.addEventListener('focusin', this.onFocus)
		this.backButtons.forEach((button) => {
			button.addEventListener('click', () => {
				this.goToQuestion((this.currentQuestion - 1).toString())
			})
		})
		this.nextButtons.forEach((button) => {
			button.addEventListener('click', () => {
				this.goToQuestion((this.currentQuestion + 1).toString())
			})
		})
		this.formEl.addEventListener('submit', this.onSubmit)
		this.progressFillEl = qsRequired('[data-progress-fill]', this)
	}

	override disconnectedCallback() {
		document.removeEventListener('focusin', this.onFocus)
	}

	onFocus(event: FocusEvent) {
		const target = targetOptional(event)
		if (!target) return
		const currentQuestion = closestOptional(target, '[data-question]')
		if (!currentQuestion) return
		this.setCurrentQuestion(
			getAttributeOrThrow('data-question', currentQuestion)
		)
	}
	async onSubmit(e: SubmitEvent) {
		e.preventDefault()
		const options = this.getOptions()
		const result = this.resultData.find(data => {
			const matchingResponse = data.responses.find(response => {
				return response.every(response => options.includes(response))
			})
			return !!matchingResponse;

		})
		this.setProgressComplete()

		if (!result) {
			throw new Error('No result found')
		}
		const stub = qsRequired<DynamicQuizStub>('dynamic-quiz-stub')
		void stub.getQuizResult(result.url, this.titleText)
	}
	getResultData(): QuizResult[] {
		const textContent = qsRequired<HTMLScriptElement>(
			'[type="application/json"][data-quiz-results]',
			this
		).textContent
		if (!textContent) throw new Error('Variant data not found')
		return JSON.parse(textContent).results
	}
	getOptions() {
		return Array.from(
			qsaRequired<HTMLInputElement>('input[type="radio"]:checked', this)
		).map((input) => {
			return input.value
		})
	}
	goToQuestion(index: string) {
		const question = Array.from(this.questions).find(
			(questionEl) =>
				getAttributeOrThrow('data-question', questionEl) === index
		)
		if (!question) return
		qsRequired('input[type="radio"]', question).focus()
	}
	setCurrentQuestion(currentQuestion: string) {
		this.setAttribute('data-initial-focus', currentQuestion)
		this.currentQuestionEl.innerHTML = currentQuestion
		this.currentQuestion = parseInt(currentQuestion)
		this.toggleBackButtons()
		this.updateProgressFill()
	}
	toggleBackButtons() {
		if (this.currentQuestion === 1) {
			this.backButtons.forEach((button) => {
				button.setAttribute('disabled', '')
			})
		} else {
			this.backButtons.forEach((button) => {
				button.removeAttribute('disabled')
			})
		}
	}

	updateProgressFill() {
		let progress = (this.currentQuestion / this.questions.length) * 100
		if (progress > 90) {
			progress = 97
		}
		this.progressFillEl.style.setProperty('--progress', `${progress}%`);
	}
	setProgressComplete() {
		this.progressFillEl.style.setProperty('--progress', '100%')
	}
}
